// Audio playback engine (§4.11): Web Audio look-ahead scheduler over the
// played timeline. Every 25 ms it schedules all note events and metronome
// clicks that start within the next 120 ms. Times derive from a single anchor
// (see scheduler.ts), so playback never drifts; the speed factor scales the
// tempo, never the playback rate, so pitch is unaffected.
//
// Usage: one engine per app (`getAudioEngine()`); call `play()` from a user
// gesture (click/keydown) — it creates/resumes the AudioContext.
//
// A context can go silent behind our back: the browser suspends or
// interrupts it (sleep, another app taking the audio device, Safari), or the
// output device changes and the context keeps rendering to the old one. So
// every gesture resumes a context that is not running, a resume that does
// not complete marks the context stale, and so does an output device
// change; a stale context is replaced at the next gesture while stopped.

import { SOUNDS, timeSigAt } from '@simplenotation/core';
import type { Score, Timeline, Track } from '@simplenotation/core';
import { barCapacity, beatGroups } from '@simplenotation/theory';
import { Metronome } from './metronome';
import { Sampler, type Voice } from './sampler';
import type { SoundKind } from './synth';
import {
  type Anchor,
  type Cursor,
  type LoopRange,
  type TickRange,
  type TimeMap,
  advanceCursor,
  audibleTracks,
  clampSpeed,
  compareCursor,
  countInPlan,
  itemsInRange,
  legatoTargets,
  loopActive,
  normalizeLoop,
  noteEndTime,
  positionAt,
  qpmAt,
  segmentOf,
  startTickFor,
  timeMapOf,
  timeOfTick,
  volumeGain,
} from './scheduler';
import { createTicker, type Ticker } from './ticker';

export type { LoopRange } from './scheduler';

export interface MetronomeSettings {
  enabled: boolean;
  /** 0…1 */
  volume: number;
  /** Click every beat, or only on each bar's downbeat. */
  frequency: 'beat' | 'bar';
}

export interface CountInSettings {
  enabled: boolean;
  /** Bars counted in before playback starts (1–4 typical). */
  bars: number;
}

export interface EngineState {
  /** Transport running (includes the count-in). */
  playing: boolean;
  paused: boolean;
  countingIn: boolean;
  /** Waiting for sounds to load/synthesize before starting. */
  loading: boolean;
  /** Position (latency compensated). */
  tick: number;
}

export interface CountInInfo {
  /** False once the count-in is over (playback proper starts). */
  active: boolean;
  /** 1-based beat being clicked (0 when inactive). */
  beat: number;
  beats: number;
  /** 1-based count-in bar. */
  bar: number;
  bars: number;
}

export interface EngineEvents {
  state: EngineState;
  ended: { tick: number };
  countin: CountInInfo;
}

export interface AudioEngineOptions {
  sampler?: Sampler;
  /** Look-ahead window in seconds (default 0.12). */
  lookahead?: number;
  /** Scheduler period in ms (default 25). */
  interval?: number;
  /** Factory for the AudioContext (tests, custom sample rates). */
  createContext?: () => AudioContext;
  /** How long play/preview wait for the context to resume, in ms (default 3000). */
  resumeTimeout?: number;
}

interface LiveVoice {
  voice: Voice;
  kind: 'note' | 'click' | 'countin';
}

interface TrackBus {
  input: GainNode;
  pan: StereoPannerNode | null;
}

type Mode = 'stopped' | 'paused' | 'loading' | 'playing';

const HIDDEN_LOOKAHEAD = 1.2;
const START_DELAY = 0.08;
const SEEK_DELAY = 0.03;

function kindOf(id: string): SoundKind | undefined {
  return SOUNDS.find((s) => s.id === id)?.kind;
}

function sourceVoice(ctx: BaseAudioContext, src: AudioScheduledSourceNode, start: number, end: number): Voice {
  const v: Voice = {
    start,
    end,
    release(at: number) {
      try {
        src.stop(Math.max(at, ctx.currentTime));
      } catch {
        /* already stopped */
      }
      v.end = Math.min(v.end, at);
    },
    cancel() {
      try {
        src.stop();
      } catch {
        /* already stopped */
      }
      v.end = ctx.currentTime;
    },
  };
  return v;
}

export class AudioEngine {
  readonly sampler: Sampler;
  private readonly lookaheadSec: number;
  private readonly intervalMs: number;
  private readonly createContext: () => AudioContext;
  private readonly resumeTimeoutMs: number;

  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private limiter: DynamicsCompressorNode | null = null;
  private previewBus: GainNode | null = null;
  private metronome: Metronome | null = null;
  private buses = new Map<string, TrackBus>();

  private score: Score | null = null;
  private tl: Timeline | null = null;
  private map: TimeMap | null = null;
  private legato: Uint8Array = new Uint8Array(0);
  private audible: boolean[] = [];

  private speedFactor = 1;
  private loopReq: LoopRange | null = null;
  private metro: MetronomeSettings = { enabled: false, volume: 0.7, frequency: 'beat' };
  private countIn: CountInSettings = { enabled: false, bars: 1 };
  private latencyMs = 0;
  private masterVolume = 1;

  private mode: Mode = 'stopped';
  private position = 0;
  private playStart = 0;
  private anchor: Anchor | null = null;
  /** Previous anchor (before a soft re-anchor), for a continuous latency-compensated position. */
  private prev: { anchor: Anchor; map: TimeMap } | null = null;
  private cursor: Cursor = { segment: 0, tick: 0 };
  private countInQueue: { time: number; info: CountInInfo }[] = [];
  private countInEnd = 0;
  private voices: LiveVoice[] = [];
  private previewVoices: Voice[] = [];
  private previewToken = 0;
  private ticker: Ticker | null = null;
  private playToken = 0;
  private visibilityHandler: (() => void) | null = null;
  private gestureHandler: (() => void) | null = null;
  private deviceHandler: (() => void) | null = null;
  /** The context may be silent for good (failed resume, output device change): replace it at the next gesture. */
  private stale = false;
  private listeners: { [K in keyof EngineEvents]: Set<(e: EngineEvents[K]) => void> } = {
    state: new Set(),
    ended: new Set(),
    countin: new Set(),
  };

  constructor(opts: AudioEngineOptions = {}) {
    this.sampler = opts.sampler ?? new Sampler({ kindOf });
    this.lookaheadSec = opts.lookahead ?? 0.12;
    this.intervalMs = opts.interval ?? 25;
    this.resumeTimeoutMs = opts.resumeTimeout ?? 3000;
    this.createContext =
      opts.createContext ??
      (() => {
        const w = globalThis as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
        const Ctor = w.AudioContext ?? w.webkitAudioContext;
        if (!Ctor) throw new Error('Web Audio is not supported in this browser');
        return new Ctor({ latencyHint: 'interactive' });
      });
  }

  // ------------------------------------------------------------------ events

  on<K extends keyof EngineEvents>(event: K, cb: (e: EngineEvents[K]) => void): () => void {
    const set = this.listeners[event] as Set<(e: EngineEvents[K]) => void>;
    set.add(cb);
    return () => set.delete(cb);
  }

  private emit<K extends keyof EngineEvents>(event: K, payload: EngineEvents[K]): void {
    for (const cb of [...(this.listeners[event] as Set<(e: EngineEvents[K]) => void>)]) {
      try {
        cb(payload);
      } catch (err) {
        console.error(err);
      }
    }
  }

  private emitState(): void {
    this.emit('state', this.state());
  }

  /** Snapshot of the transport state. */
  state(): EngineState {
    return {
      playing: this.mode === 'playing',
      paused: this.mode === 'paused',
      countingIn: this.countingIn,
      loading: this.mode === 'loading',
      tick: this.currentTick(),
    };
  }

  // ----------------------------------------------------------------- context

  /** The AudioContext (created lazily by play/preview). */
  get context(): AudioContext | null {
    return this.ctx;
  }

  private ensureContext(): AudioContext {
    if (this.ctx && this.ctx.state !== 'closed') return this.ctx;
    const ctx = this.createContext();
    this.ctx = ctx;
    this.limiter = ctx.createDynamicsCompressor();
    this.limiter.threshold.value = -8;
    this.limiter.knee.value = 6;
    this.limiter.ratio.value = 12;
    this.limiter.attack.value = 0.003;
    this.limiter.release.value = 0.2;
    this.limiter.connect(ctx.destination);
    this.master = ctx.createGain();
    this.master.gain.value = this.masterVolume * this.masterVolume;
    this.master.connect(this.limiter);
    this.previewBus = ctx.createGain();
    this.previewBus.connect(this.master);
    this.metronome = new Metronome(ctx, this.master);
    this.metronome.setVolume(this.metro.volume);
    this.buses.clear();
    if (this.score) this.updateBuses();
    // iOS/Safari unlock: play one silent sample inside the gesture.
    try {
      const b = ctx.createBuffer(1, 1, ctx.sampleRate);
      const s = ctx.createBufferSource();
      s.buffer = b;
      s.connect(ctx.destination);
      s.start(0);
    } catch {
      /* ignore */
    }
    this.listen();
    return ctx;
  }

  private listen(): void {
    if (typeof navigator !== 'undefined') {
      // iOS: let Web Audio sound with the ring/silent switch on, like media elements do.
      const nav = navigator as Navigator & { audioSession?: { type: string } };
      try {
        if (nav.audioSession?.type === 'auto') nav.audioSession.type = 'playback';
      } catch {
        /* ignore */
      }
      const devices = nav.mediaDevices;
      if (devices && typeof devices.addEventListener === 'function' && !this.deviceHandler) {
        this.deviceHandler = () => {
          this.stale = true;
        };
        devices.addEventListener('devicechange', this.deviceHandler);
      }
    }
    if (typeof document === 'undefined') return;
    if (!this.visibilityHandler) {
      this.visibilityHandler = () => {
        if (document.visibilityState === 'visible' && this.ctx && this.ctx.state !== 'running') void this.ctx.resume().catch(() => undefined);
        this.pump();
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }
    if (!this.gestureHandler) {
      // Any click or key press may revive a context the browser suspended.
      this.gestureHandler = () => this.wake(false);
      for (const type of ['pointerdown', 'keydown', 'touchend']) document.addEventListener(type, this.gestureHandler, { capture: true, passive: true });
    }
  }

  /**
   * From a user gesture: replace a stale context while nothing is sounding,
   * then resume the context. `create` makes one if there is none yet.
   */
  private wake(create: boolean): AudioContext | null {
    if (this.stale && (this.mode === 'stopped' || this.mode === 'paused')) void this.closeContext();
    const ctx = create ? this.ensureContext() : this.ctx;
    if (ctx && ctx.state !== 'running') void ctx.resume().catch(() => undefined);
    return ctx;
  }

  /** Wait (bounded) for the context to run; a context that won't is marked stale. */
  private async running(ctx: AudioContext): Promise<boolean> {
    if (ctx.state === 'running') return true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    await Promise.race([
      ctx.resume().catch(() => undefined),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, this.resumeTimeoutMs);
      }),
    ]);
    clearTimeout(timer);
    if ((ctx.state as string) === 'running') return true;
    if (ctx === this.ctx) this.stale = true;
    return false;
  }

  /** Drop the context and everything built on it (the next play/preview creates a new one). */
  private closeContext(): Promise<void> {
    const ctx = this.ctx;
    this.stale = false;
    if (!ctx) return Promise.resolve();
    this.previewToken++;
    this.previewVoices = [];
    this.voices = [];
    this.metronome?.dispose();
    this.metronome = null;
    this.buses.clear();
    this.master = null;
    this.limiter = null;
    this.previewBus = null;
    this.ctx = null;
    return ctx.close().catch(() => undefined);
  }

  private outputLatency(): number {
    const c = this.ctx as (AudioContext & { outputLatency?: number }) | null;
    const auto = c ? (c.outputLatency || c.baseLatency || 0) : 0;
    return Math.max(0, auto + this.latencyMs / 1000);
  }

  private lookahead(): number {
    return typeof document !== 'undefined' && document.visibilityState === 'hidden' ? HIDDEN_LOOKAHEAD : this.lookaheadSec;
  }

  // ------------------------------------------------------------------- score

  /** Set the document and its played timeline (call again after every change). */
  setScore(score: Score, timeline: Timeline): void {
    const tlChanged = timeline !== this.tl;
    const soundsBefore = this.score ? this.score.tracks.map((t) => t.sound).join('|') : '';
    if (tlChanged && this.mode === 'playing' && this.anchor && this.tl) {
      this.score = score;
      this.audible = audibleTracks(score.tracks);
      this.reanchor({ tl: timeline });
    } else {
      this.score = score;
      this.audible = audibleTracks(score.tracks);
      if (tlChanged) this.applyTimeline(timeline);
    }
    this.updateBuses();
    if (this.mode !== 'playing') this.position = Math.min(this.position, timeline.totalTicks);
    if (score.tracks.map((t) => t.sound).join('|') !== soundsBefore) this.preload();
  }

  private applyTimeline(tl: Timeline): void {
    this.tl = tl;
    this.map = timeMapOf(tl);
    this.legato = legatoTargets(tl.events);
  }

  /** Sound ids used by the score (audible tracks first). */
  private soundIds(audibleOnly: boolean): string[] {
    if (!this.score) return [];
    return [...new Set(this.score.tracks.filter((_, i) => !audibleOnly || this.audible[i]).map((t) => t.sound))];
  }

  /**
   * Load/synthesize sounds ahead of time (no AudioContext needed). Called
   * automatically by setScore when the set of sounds changes.
   */
  preload(soundIds?: string[]): Promise<void> {
    const ids = soundIds ?? this.soundIds(false);
    const ctx = this.ctx;
    return Promise.all(ids.map((id) => (ctx ? this.sampler.bufferAsync(ctx, id) : this.sampler.load(id)).catch((e) => console.warn('sound load failed', id, e)))).then(
      () => undefined,
    );
  }

  private updateBuses(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || !this.score) return;
    const seen = new Set<string>();
    this.score.tracks.forEach((t, i) => {
      seen.add(t.id);
      const bus = this.busFor(t);
      const g = this.audible[i] ? volumeGain(t.volume) : 0;
      bus.input.gain.setTargetAtTime(g, ctx.currentTime, 0.012);
      bus.pan?.pan.setTargetAtTime(Math.max(-1, Math.min(1, t.pan ?? 0)), ctx.currentTime, 0.02);
    });
    for (const [id, bus] of this.buses) {
      if (seen.has(id)) continue;
      bus.input.gain.setTargetAtTime(0, ctx.currentTime, 0.02);
      this.buses.delete(id);
      setTimeout(() => {
        bus.input.disconnect();
        bus.pan?.disconnect();
      }, 3000);
    }
  }

  private busFor(t: Track): TrackBus {
    let bus = this.buses.get(t.id);
    if (!bus) {
      const ctx = this.ctx!;
      const input = ctx.createGain();
      input.gain.value = 0;
      let pan: StereoPannerNode | null = null;
      try {
        pan = ctx.createStereoPanner();
        input.connect(pan).connect(this.master!);
      } catch {
        input.connect(this.master!);
      }
      bus = { input, pan };
      this.buses.set(t.id, bus);
    }
    return bus;
  }

  // --------------------------------------------------------------- transport

  get playing(): boolean {
    return this.mode === 'playing';
  }

  get paused(): boolean {
    return this.mode === 'paused';
  }

  get loading(): boolean {
    return this.mode === 'loading';
  }

  /** True while the count-in is sounding. */
  get countingIn(): boolean {
    if (this.mode !== 'playing' || !this.ctx || this.countInEnd === 0) return false;
    return this.ctx.currentTime - this.outputLatency() < this.countInEnd;
  }

  get speed(): number {
    return this.speedFactor;
  }

  /**
   * Start playback at `fromTick` (default: the current position). Must be
   * called from a user gesture the first time. While playing, seeks.
   */
  async play(fromTick?: number): Promise<void> {
    const ctx = this.wake(true)!;
    if (this.mode === 'playing') {
      if (fromTick !== undefined) this.seek(fromTick);
      return;
    }
    if (!this.score || !this.tl || this.tl.totalTicks <= 0) return;
    const token = ++this.playToken;
    const requested = fromTick ?? this.position;
    const before = this.mode;
    this.mode = 'loading';
    this.emitState();
    try {
      void this.preload(this.soundIds(false));
      await this.sampler.ensure(ctx, this.soundIds(true));
    } catch (err) {
      console.warn('sound load failed', err);
    }
    if (token !== this.playToken) return;
    const ok = await this.running(ctx);
    if (token !== this.playToken || !this.tl) return;
    if (!ok) {
      this.mode = before === 'paused' ? 'paused' : 'stopped';
      this.emitState();
      throw new Error('the browser didn’t let this tab play sound. Press play again.');
    }
    this.startAt(requested);
  }

  private startAt(requested: number): void {
    const ctx = this.ctx!;
    const tl = this.tl!;
    const loop = normalizeLoop(this.loopReq, tl.totalTicks);
    const start = startTickFor(requested, tl.totalTicks, loop);
    this.playStart = start;
    let t0 = ctx.currentTime + START_DELAY;
    this.countInQueue = [];
    this.countInEnd = 0;
    if (this.countIn.enabled && this.countIn.bars > 0) {
      const plan = this.countInFor(start);
      const bars = Math.max(1, Math.floor(this.countIn.bars));
      for (const c of plan.clicks) {
        const time = t0 + c.offset;
        const src = this.metronome!.click(time, c.accent);
        this.voices.push({ kind: 'countin', voice: sourceVoice(ctx, src, time, time + 0.1) });
        this.countInQueue.push({ time, info: { active: true, beat: c.beat + 1, beats: plan.clicks.length, bar: c.bar + 1, bars } });
      }
      t0 += plan.duration;
      this.countInEnd = t0;
      this.countInQueue.push({ time: t0, info: { active: false, beat: 0, beats: plan.clicks.length, bar: 0, bars } });
    }
    this.anchor = { time: t0, tick: start, speed: this.speedFactor, loop };
    this.prev = null;
    this.cursor = { segment: 0, tick: start };
    this.mode = 'playing';
    this.ticker ??= createTicker(this.intervalMs, () => this.pump());
    this.ticker.start();
    this.emitState();
    this.pump();
  }

  private countInFor(start: number) {
    const tl = this.tl!;
    const score = this.score!;
    const pbIdx = tl.bars.findIndex((b, i) => b.startTick <= start && (i === tl.bars.length - 1 || tl.bars[i + 1].startTick > start));
    const barIdx = tl.bars[Math.max(0, pbIdx)]?.barIdx ?? 0;
    const ts = timeSigAt(score, barIdx);
    return countInPlan({
      bars: this.countIn.bars,
      barTicks: barCapacity(ts),
      groups: beatGroups(ts),
      qpm: qpmAt(tl, start),
      speed: this.speedFactor,
    });
  }

  /** Pause, keeping the position. */
  pause(): void {
    if (this.mode === 'loading') {
      this.playToken++;
      this.mode = 'paused';
      this.emitState();
      return;
    }
    if (this.mode !== 'playing') return;
    this.position = this.currentTick();
    this.halt();
    this.mode = 'paused';
    this.emitState();
  }

  /** Stop and return to the tick playback was started from. */
  stop(): void {
    const active = this.mode !== 'stopped';
    this.playToken++;
    if (this.mode === 'playing') this.halt();
    if (active) this.position = this.playStart;
    this.mode = 'stopped';
    this.emitState();
  }

  /** Jump to a played tick (keeps playing if playing). */
  seek(tick: number): void {
    const total = this.tl?.totalTicks ?? 0;
    const t = Math.max(0, Math.min(Number.isFinite(tick) ? tick : 0, Math.max(0, total - 1)));
    if (this.mode !== 'playing' || !this.ctx || !this.anchor) {
      this.position = t;
      if (this.mode === 'stopped') this.playStart = t;
      this.emitState();
      return;
    }
    const ctx = this.ctx;
    const now = ctx.currentTime;
    if (now < this.anchor.time) {
      // During the count-in: just move the start.
      this.dropVoices((v) => v.kind !== 'countin', now);
      this.anchor = { ...this.anchor, tick: t };
      this.cursor = { segment: 0, tick: t };
      this.playStart = t;
      this.emitState();
      return;
    }
    this.dropVoices((v) => v.kind !== 'countin', now, 0.03);
    this.anchor = { time: now + SEEK_DELAY, tick: t, speed: this.speedFactor, loop: normalizeLoop(this.loopReq, total) };
    this.prev = null;
    this.cursor = { segment: 0, tick: t };
    this.emitState();
    this.pump();
  }

  /** Position in played ticks, compensated for output + user latency; the start tick during the count-in. */
  currentTick(): number {
    if (this.mode !== 'playing' || !this.anchor || !this.ctx || !this.map) return this.position;
    const t = this.ctx.currentTime - this.outputLatency();
    let tick: number;
    if (t < this.anchor.time) {
      tick = this.prev && t >= this.prev.anchor.time ? positionAt(this.prev.anchor, this.prev.map, t).tick : this.anchor.tick;
    } else tick = positionAt(this.anchor, this.map, t).tick;
    return Math.max(0, Math.min(tick, this.tl?.totalTicks ?? tick));
  }

  /** Tempo factor, 0.25…2 (pitch is unaffected). */
  setSpeed(factor: number): void {
    const s = clampSpeed(factor);
    if (s === this.speedFactor) return;
    if (this.mode === 'playing' && this.anchor) this.reanchor({ speed: s });
    else this.speedFactor = s;
  }

  /** Loop [startTick, endTick) in played ticks, or null. */
  setLoop(range: LoopRange | null): void {
    this.loopReq = range ? { startTick: range.startTick, endTick: range.endTick } : null;
    if (this.mode !== 'playing' || !this.anchor || !this.ctx || !this.tl) return;
    const loop = normalizeLoop(this.loopReq, this.tl.totalTicks);
    const pos = positionAt(this.anchor, this.map!, this.ctx.currentTime).tick;
    if (loop && pos >= loop.endTick) this.seek(loop.startTick);
    else this.reanchor({ loop });
  }

  setMetronome(m: Partial<MetronomeSettings>): void {
    const wasEnabled = this.metro.enabled;
    this.metro = { ...this.metro, ...m };
    this.metronome?.setVolume(this.metro.volume);
    if (wasEnabled && !this.metro.enabled && this.ctx) {
      const now = this.ctx.currentTime;
      this.dropVoices((v) => v.kind === 'click' && v.voice.start >= now, now);
    }
  }

  getMetronome(): MetronomeSettings {
    return { ...this.metro };
  }

  setCountIn(c: Partial<CountInSettings>): void {
    this.countIn = { ...this.countIn, ...c };
  }

  getCountIn(): CountInSettings {
    return { ...this.countIn };
  }

  /** User audio-latency offset (ms) subtracted from the reported position. */
  setLatencyMs(ms: number): void {
    this.latencyMs = Number.isFinite(ms) ? ms : 0;
  }

  /** 0…1 (quadratic taper). */
  setMasterVolume(v: number): void {
    this.masterVolume = Math.max(0, Math.min(1, Number.isFinite(v) ? v : 1));
    if (this.ctx && this.master) this.master.gain.setTargetAtTime(this.masterVolume * this.masterVolume, this.ctx.currentTime, 0.015);
  }

  /**
   * Sound a few notes now (play-on-select / note input). Cuts the previous
   * preview. Creates the AudioContext if needed (call from a user gesture).
   */
  async previewNotes(midis: number[], sound: string, durationSec = 1): Promise<void> {
    const ctx = this.wake(true)!;
    const token = ++this.previewToken;
    const now = ctx.currentTime;
    for (const v of this.previewVoices) v.release(now, 0.04);
    this.previewVoices = [];
    if (!this.sampler.buffer(ctx, sound)) {
      await this.sampler.bufferAsync(ctx, sound).catch(() => null);
      if (token !== this.previewToken) return;
    }
    if (!(await this.running(ctx)) || token !== this.previewToken || ctx !== this.ctx) return;
    const when = ctx.currentTime + 0.01;
    for (const midi of midis) {
      const v = this.sampler.startVoice(ctx, this.previewBus!, sound, { midi, when, duration: durationSec, velocity: 0.75 });
      if (v) this.previewVoices.push(v);
    }
  }

  /** Stop everything and release the AudioContext. */
  async dispose(): Promise<void> {
    this.stop();
    this.ticker?.dispose();
    this.ticker = null;
    if (typeof document !== 'undefined') {
      if (this.visibilityHandler) document.removeEventListener('visibilitychange', this.visibilityHandler);
      if (this.gestureHandler) for (const type of ['pointerdown', 'keydown', 'touchend']) document.removeEventListener(type, this.gestureHandler, { capture: true });
    }
    if (this.deviceHandler && typeof navigator !== 'undefined') navigator.mediaDevices?.removeEventListener('devicechange', this.deviceHandler);
    this.visibilityHandler = null;
    this.gestureHandler = null;
    this.deviceHandler = null;
    this.sampler.dispose();
    await this.closeContext();
  }

  // --------------------------------------------------------------- internals

  /** Stop the scheduler and fade out everything sounding. */
  private halt(): void {
    this.ticker?.stop();
    const now = this.ctx?.currentTime ?? 0;
    this.dropVoices(() => true, now, 0.03);
    this.anchor = null;
    this.prev = null;
    this.countInQueue = [];
    this.countInEnd = 0;
  }

  /** Cancel voices that have not started; fade out (or let ring) the rest. */
  private dropVoices(pred: (v: LiveVoice) => boolean, now: number, fade?: number): void {
    const keep: LiveVoice[] = [];
    for (const v of this.voices) {
      if (!pred(v)) {
        keep.push(v);
        continue;
      }
      if (v.voice.start >= now) v.voice.cancel();
      else if (fade !== undefined) v.voice.release(now, fade);
      else keep.push(v);
    }
    this.voices = keep;
  }

  /**
   * Re-derive the anchor at the current audio time after a timeline, speed
   * or loop change: voices already sounding keep ringing, voices not yet
   * started are cancelled and everything is rescheduled from here.
   */
  private reanchor(change: { tl?: Timeline; speed?: number; loop?: LoopRange | null }): void {
    const ctx = this.ctx!;
    const a = this.anchor!;
    const now = ctx.currentTime;
    const oldMap = this.map!;
    if (change.tl) this.applyTimeline(change.tl);
    if (change.speed !== undefined) this.speedFactor = change.speed;
    const total = this.tl!.totalTicks;
    const loop = change.loop !== undefined ? change.loop : normalizeLoop(this.loopReq, total);
    this.dropVoices((v) => v.kind !== 'countin', now);
    if (now < a.time) {
      // Still counting in: keep the count-in, update what follows.
      const tick = Math.min(a.tick, Math.max(0, total - 1));
      this.anchor = { time: a.time, tick, speed: this.speedFactor, loop };
      this.cursor = { segment: 0, tick };
      return;
    }
    const pos = positionAt(a, oldMap, now);
    const tick = Math.min(pos.tick, total);
    this.prev = { anchor: a, map: oldMap };
    this.anchor = { time: now, tick, speed: this.speedFactor, loop };
    this.cursor = { segment: 0, tick };
    this.pump();
  }

  private pump(): void {
    const ctx = this.ctx;
    const a = this.anchor;
    const tl = this.tl;
    const map = this.map;
    if (!ctx || !a || !tl || !map || this.mode !== 'playing') return;
    const now = ctx.currentTime;
    const heard = now - this.outputLatency();

    if (this.voices.length > 32) this.voices = this.voices.filter((v) => v.voice.end > now);

    // Count-in notifications as they become audible.
    while (this.countInQueue.length && this.countInQueue[0].time <= heard) {
      const { info } = this.countInQueue.shift()!;
      this.emit('countin', info);
      if (!info.active) this.emitState();
    }

    // After a stall (background tab, debugger) skip what is already past.
    const nowPos = positionAt(a, map, now);
    if (now >= a.time && compareCursor(this.cursor, nowPos) < 0) this.cursor = nowPos;

    const { ranges, cursor } = advanceCursor(a, map, this.cursor, now + this.lookahead());
    this.cursor = cursor;
    for (const r of ranges) this.scheduleRange(r, a, map, tl, ctx);

    if (!loopActive(a)) {
      const endTime = timeOfTick(a, map, segmentOf(a, map, 0), tl.totalTicks);
      if (heard >= endTime) this.finish();
    }
  }

  private scheduleRange(r: TickRange, a: Anchor, map: TimeMap, tl: Timeline, ctx: AudioContext): void {
    const score = this.score!;
    for (const s of itemsInRange(tl.events, (e) => e.startTick, r, a, map)) {
      const e = s.item;
      if (!this.audible[e.track]) continue;
      const track = score.tracks[e.track];
      if (!track) continue;
      if (!this.sampler.buffer(ctx, track.sound)) {
        void this.sampler.bufferAsync(ctx, track.sound).catch(() => null);
        continue;
      }
      const end = noteEndTime(a, map, s.segment, e);
      const v = this.sampler.startVoice(ctx, this.busFor(track).input, track.sound, {
        midi: e.midi,
        when: s.time,
        duration: end - s.time,
        velocity: e.velocity,
        dead: e.dead,
        palmMute: e.palmMute,
        harmonic: e.harmonic,
        fret: e.fret,
        bend: e.bend,
        vibrato: e.vibrato,
        legato: this.legato[s.index] === 1,
      });
      if (v) this.voices.push({ voice: v, kind: 'note' });
    }
    if (this.metro.enabled && this.metronome) {
      for (const s of itemsInRange(tl.clicks, (c) => c.tick, r, a, map)) {
        if (this.metro.frequency === 'bar' && !s.item.accent) continue;
        const src = this.metronome.click(s.time, s.item.accent);
        this.voices.push({ kind: 'click', voice: sourceVoice(ctx, src, s.time, s.time + 0.1) });
      }
    }
  }

  private finish(): void {
    const total = this.tl?.totalTicks ?? 0;
    this.ticker?.stop();
    this.anchor = null;
    this.prev = null;
    this.countInQueue = [];
    this.countInEnd = 0;
    this.mode = 'stopped';
    this.position = this.playStart;
    this.emit('ended', { tick: total });
    this.emitState();
  }
}

let shared: AudioEngine | null = null;

/** The app-wide engine (created on first use; no AudioContext until play/preview). */
export function getAudioEngine(): AudioEngine {
  shared ??= new AudioEngine();
  return shared;
}
